import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, ArrowRight, Upload, CheckCircle, AlertCircle, Info, ExternalLink, Plus, Trash2 } from 'lucide-react';

const O_LEVEL_DIVISION_OPTIONS = [
  { value: 'Division I',   label: 'Division I (Highest) — 7–17 aggregate points' },
  { value: 'Division II',  label: 'Division II — 18–21 aggregate points' },
  { value: 'Division III', label: 'Division III — 22–25 aggregate points' },
  { value: 'Division IV',  label: 'Division IV (Minimum Pass) — 26–33 aggregate points' },
  { value: 'Division 0',   label: 'Division Zero (Failure) — 34+ aggregate points' },
];

const GRADE_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'S'];

const ApplicationForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [age, setAge] = useState(null);
  const [admissionSettings, setAdmissionSettings] = useState({ admissions_open: true, closed_message: '' });
  const [checkingAdmission, setCheckingAdmission] = useState(true);
  const [dbError, setDbError] = useState(false);
  const [eligibilityCriteria, setEligibilityCriteria] = useState([]);
  // null = not yet chosen, true = did A-levels, false = alternative curriculum
  const [hasALevel, setHasALevel] = useState(null);
  const [emailDuplicate, setEmailDuplicate] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [step5Ready, setStep5Ready] = useState(false);

  const [formData, setFormData] = useState({
    last_name: '',
    first_name: '',
    middle_name: '',
    nida_passport: '',
    id_expiry: '',
    date_of_birth: '',
    birth_place: '',
    email: '',
    mobile: '255',
    whatsapp: '',
    address: '',
    currently_employed: null,
    employer_name: '',
    o_level_year: '',
    o_level_school: '',
    o_level_division: '',
    o_level_certificate: null,
    o_level_certificate_filename: '',
    o_level_certificate_type: '',
    a_level_year: '',
    a_level_school: '',
    a_level_curriculum: '',
    a_level_subjects: [
      { subject: '', grade: '' },
      { subject: '', grade: '' },
      { subject: '', grade: '' },
    ],
    a_level_certificate: null,
    a_level_certificate_filename: '',
    a_level_certificate_type: '',
    llb_graduation_date: '',
    llb_class: '',
    llb_gpa_scale: '',
    llb_cgpa: '',
    llb_institute: '',
    llb_years_attended: '',
    llb_certificate: null,
    llb_certificate_filename: '',
    llb_certificate_type: '',
    nida_passport_copy: null,
    nida_passport_copy_filename: '',
    nida_passport_copy_type: '',
    // Referees
    referee1_name: '',
    referee1_title: '',
    referee1_institution: '',
    referee1_email: '',
    referee1_telephone: '',
    referee2_name: '',
    referee2_title: '',
    referee2_institution: '',
    referee2_email: '',
    referee2_telephone: '',
    personal_statement: '',
    // Declarations
    declaration_date: new Date().toISOString().split('T')[0],
    terms_accepted: false,
    declaration_responses: {},
  });

  const [declarations, setDeclarations] = useState([]);
  const [activeIntakeId, setActiveIntakeId] = useState(null);
  const [gpaMins, setGpaMins] = useState({ 4: 2.8, 5: 3.3 });

  const totalSteps = 5;
  const progress = ((step - 1) / totalSteps) * 100;

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const healthRes = await axios.get(`${API}/health`).catch(() => null);
        if (!healthRes || healthRes.status !== 200 || healthRes.data?.status !== 'ok') {
          setDbError(true);
          return;
        }
        const [admissionRes, configRes, intakeRes, scoringRes] = await Promise.all([
          axios.get(`${API}/admission-settings`),
          axios.get(`${API}/site-config`),
          axios.get(`${API}/intake-periods/active`).catch(() => ({ data: null })),
          axios.get(`${API}/scoring-config`).catch(() => ({ data: { gpa_min_4: 2.8, gpa_min_5: 3.3 } }))
        ]);
        setAdmissionSettings(admissionRes.data);
        setEligibilityCriteria(configRes.data.eligibility_criteria || []);
        setGpaMins({ 4: scoringRes.data.gpa_min_4 ?? 2.8, 5: scoringRes.data.gpa_min_5 ?? 3.3 });
        if (intakeRes.data && intakeRes.data.id) {
          setActiveIntakeId(intakeRes.data.id);
        }
        const configDeclarations = configRes.data.declarations || [];
        setDeclarations(configDeclarations.sort((a, b) => (a.order || 0) - (b.order || 0)));
        const initialResponses = {};
        configDeclarations.forEach(d => { initialResponses[d.id] = false; });
        setFormData(prev => ({ ...prev, declaration_responses: initialResponses }));
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
        setAdmissionSettings({ admissions_open: true, closed_message: '' });
      } finally {
        setCheckingAdmission(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (formData.date_of_birth) {
      setAge(calculateAge(formData.date_of_birth));
    } else {
      setAge(null);
    }
  }, [formData.date_of_birth]);

  useEffect(() => {
    if (step === totalSteps) {
      setStep5Ready(false);
      const t = setTimeout(() => setStep5Ready(true), 400);
      return () => clearTimeout(t);
    }
  }, [step]);

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'email') setEmailDuplicate(false);
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEmailBlur = async () => {
    const email = formData.email.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setEmailChecking(true);
    try {
      const res = await axios.get(`${API}/applications/check-email`, { params: { email } });
      setEmailDuplicate(res.data.duplicate);
    } catch {
      // silently ignore — backend will catch it at submission
    } finally {
      setEmailChecking(false);
    }
  };

  const handleMobileChange = (e) => {
    // Only allow digits after the 255 prefix
    const digits = e.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, mobile: '255' + digits }));
  };

  const handleSubjectChange = (index, field, value) => {
    const newSubjects = [...formData.a_level_subjects];
    newSubjects[index] = { ...newSubjects[index], [field]: value };
    setFormData(prev => ({ ...prev, a_level_subjects: newSubjects }));
  };

  const addSubject = () => {
    setFormData(prev => ({
      ...prev,
      a_level_subjects: [...prev.a_level_subjects, { subject: '', grade: '' }],
    }));
  };

  const removeSubject = (idx) => {
    setFormData(prev => ({
      ...prev,
      a_level_subjects: prev.a_level_subjects.filter((_, i) => i !== idx),
    }));
  };

  const handleFileUpload = (name, file) => {
    if (!file) return;
    const maxSize = 1048576;
    if (file.size > maxSize) { toast.error('File size must be less than 1MB'); return; }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) { toast.error('Only PDF, JPG, and PNG files are allowed'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        [name]: reader.result,
        [`${name}_filename`]: file.name,
        [`${name}_type`]: file.type
      }));
      toast.success('File uploaded successfully');
    };
    reader.readAsDataURL(file);
  };


  const validateStep = () => {
    if (step === 1) {
      if (emailDuplicate) {
        toast.error('This email has already been used to apply for the current intake');
        return false;
      }
      const required = ['last_name', 'first_name', 'nida_passport', 'date_of_birth', 'birth_place', 'email', 'mobile', 'address'];
      for (let field of required) {
        if (!formData[field] || formData[field] === '255') {
          toast.error('Please fill all required fields');
          return false;
        }
      }
      // Mobile must have digits after 255
      if (formData.mobile === '255' || formData.mobile.length <= 3) {
        toast.error('Please enter a valid mobile number');
        return false;
      }
      if (!formData.nida_passport_copy) {
        toast.error('Please upload NIDA/Passport copy (MANDATORY)');
        return false;
      }
      if (formData.id_expiry) {
        const expiryDate = new Date(formData.id_expiry);
        if (expiryDate <= new Date()) {
          toast.error('Passport expiry date must be in the future');
          return false;
        }
      }
    } else if (step === 2) {
      if (!formData.o_level_year || !formData.o_level_school) {
        toast.error('Please fill all required O Level fields');
        return false;
      }
      if (!formData.o_level_certificate) {
        toast.error('Please upload O Level certificate (MANDATORY)');
        return false;
      }
    } else if (step === 3) {
      // A-level section
      if (hasALevel === null) {
        toast.error('Please indicate whether you completed A Levels');
        return false;
      }
      // All 3 required subject rows must be filled
      const filledSubjects = formData.a_level_subjects.filter(s => s.subject.trim() && s.grade);
      if (filledSubjects.length < 3) {
        toast.error('Please provide at least 3 subjects with grades');
        return false;
      }
      if (!hasALevel && !formData.a_level_curriculum.trim()) {
        toast.error('Please specify the curriculum you completed');
        return false;
      }
      if (hasALevel && !formData.a_level_certificate) {
        toast.error('Please upload A Level certificate (MANDATORY)');
        return false;
      }
      // LLB section
      const llbRequired = ['llb_graduation_date', 'llb_gpa_scale', 'llb_cgpa', 'llb_institute', 'llb_years_attended'];
      for (let field of llbRequired) {
        if (!formData[field]) {
          toast.error('Please fill all required LLB fields');
          return false;
        }
      }
      const scale = parseInt(formData.llb_gpa_scale);
      const minGpa = gpaMins[scale] ?? (scale === 4 ? 2.8 : 3.3);
      const gpa = parseFloat(formData.llb_cgpa);
      if (isNaN(gpa) || gpa <= 0 || gpa > scale) {
        toast.error(`CGPA must be between 0 and ${scale}`);
        return false;
      }
      if (!formData.llb_certificate) {
        toast.error('Please upload LLB certificate/transcript (MANDATORY)');
        return false;
      }
    } else if (step === 4) {
      const ref1 = ['referee1_name', 'referee1_title', 'referee1_institution', 'referee1_email', 'referee1_telephone'];
      const ref2 = ['referee2_name', 'referee2_title', 'referee2_institution', 'referee2_email', 'referee2_telephone'];
      for (let field of ref1) {
        if (!formData[field]) { toast.error('Please fill all required Referee 1 fields'); return false; }
      }
      for (let field of ref2) {
        if (!formData[field]) { toast.error('Please fill all required Referee 2 fields'); return false; }
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.referee1_email)) { toast.error('Please enter a valid email for Referee 1'); return false; }
      if (!emailRegex.test(formData.referee2_email)) { toast.error('Please enter a valid email for Referee 2'); return false; }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== totalSteps) return;

    const mandatoryDeclarations = declarations.filter(d => d.mandatory !== false);
    for (const declaration of mandatoryDeclarations) {
      if (!formData.declaration_responses[declaration.id]) {
        toast.error(`Please confirm declaration: "${declaration.text.substring(0, 50)}..."`);
        return;
      }
    }

    setLoading(true);

    try {
      const submissionData = {
        full_name: `${formData.first_name} ${formData.middle_name ? formData.middle_name + ' ' : ''}${formData.last_name}`,
        email: formData.email,
        phone: formData.mobile,
        date_of_birth: formData.date_of_birth,
        nationality: 'Tanzanian',
        address: formData.address,
        llb_status: 'completed',
        llb_gpa: parseFloat(formData.llb_cgpa),
        llb_gpa_scale: parseInt(formData.llb_gpa_scale) || 5,
        llb_certificate: formData.llb_certificate,
        llb_certificate_filename: formData.llb_certificate_filename,
        llb_certificate_type: formData.llb_certificate_type,
        // a_level_division kept for backward-compat — encode has_a_level status
        a_level_division: hasALevel ? 'A-Level' : ('Equivalent: ' + formData.a_level_curriculum),
        a_level_certificate: formData.a_level_certificate,
        a_level_certificate_filename: formData.a_level_certificate_filename,
        a_level_certificate_type: formData.a_level_certificate_type,
        nida_passport_copy: formData.nida_passport_copy,
        nida_passport_copy_filename: formData.nida_passport_copy_filename,
        nida_passport_copy_type: formData.nida_passport_copy_type,
        // Referees
        referee1_name: formData.referee1_name,
        referee1_title: formData.referee1_title,
        referee1_institution: formData.referee1_institution,
        referee1_email: formData.referee1_email,
        referee1_telephone: formData.referee1_telephone,
        referee2_name: formData.referee2_name,
        referee2_title: formData.referee2_title,
        referee2_institution: formData.referee2_institution,
        referee2_email: formData.referee2_email,
        referee2_telephone: formData.referee2_telephone,
        personal_statement: formData.personal_statement || '',
        declaration_info_true: formData.declaration_responses['info_true'] || false,
        declaration_competitive: formData.declaration_responses['competitive'] || false,
        declaration_aptitude_tests: formData.declaration_responses['aptitude_tests'] || false,
        declaration_terms_agreed: formData.declaration_responses['terms_agreed'] || false,
        declaration_date: formData.declaration_date,
        additional_qualifications: JSON.stringify({
          personal_details: {
            last_name: formData.last_name,
            first_name: formData.first_name,
            middle_name: formData.middle_name,
            nida_passport: formData.nida_passport,
            id_expiry: formData.id_expiry,
            birth_place: formData.birth_place,
            whatsapp: formData.whatsapp,
          },
          o_levels: {
            year: formData.o_level_year,
            school: formData.o_level_school,
            division: formData.o_level_division,
          },
          a_levels: {
            year: formData.a_level_year,
            school: formData.a_level_school,
            has_a_level: hasALevel,
            curriculum: formData.a_level_curriculum,
            subjects: formData.a_level_subjects,
          },
          llb: {
            graduation_date: formData.llb_graduation_date,
            class: formData.llb_class,
            gpa_scale: parseInt(formData.llb_gpa_scale) || 5,
            institute: formData.llb_institute,
            years_attended: formData.llb_years_attended,
          },
          age: age,
          declaration_responses: formData.declaration_responses,
        }),
        additional_certificate: formData.o_level_certificate,
        additional_certificate_filename: formData.o_level_certificate_filename,
        additional_certificate_type: formData.o_level_certificate_type,
        ...(activeIntakeId ? { intake_id: activeIntakeId } : {}),
        currently_employed: formData.currently_employed,
        employer_name: formData.currently_employed ? (formData.employer_name || null) : null,
      };

      await axios.post(`${API}/applications`, submissionData);

      toast.success('Application received. You will receive a confirmation email shortly.');
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (dbError) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-sm p-10 text-center">
          <AlertCircle className="w-12 h-12 text-secondary mx-auto mb-4" />
          <h2 className="text-2xl font-playfair font-bold text-primary mb-3">Technical Difficulties</h2>
          <p className="text-gray-600 mb-6">
            We are currently experiencing technical issues and are unable to accept applications at this time.
            Please try again shortly or contact us at <a href="mailto:academy@fbattorneys.co.tz" className="text-secondary underline">academy@fbattorneys.co.tz</a>.
          </p>
          <button onClick={() => navigate('/')} className="btn-primary text-sm">Return to Home</button>
        </div>
      </div>
    );
  }

  if (checkingAdmission) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-xl font-playfair">Loading...</div>
      </div>
    );
  }

  if (!admissionSettings.admissions_open) {
    return (
      <div className="min-h-screen bg-muted py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 text-center">
            <div className="mb-6"><AlertCircle className="w-16 h-16 mx-auto text-secondary" /></div>
            <h1 className="text-3xl font-playfair font-bold text-primary mb-4">Admissions Closed</h1>
            <div className="text-gray-600 mb-8 whitespace-pre-line">
              {admissionSettings.closed_message || 'Admissions are currently closed. Please check back later for the next intake period.'}
            </div>
            <Button onClick={() => navigate('/')} className="bg-secondary hover:bg-secondary/90">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // Derived GPA min label for display
  const gpaMinLabel = formData.llb_gpa_scale ? String(gpaMins[parseInt(formData.llb_gpa_scale)] ?? 3.3) : '3.3';

  return (
    <div className="min-h-screen bg-muted py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-playfair font-bold text-primary mb-2">Application Form</h1>
          <p className="text-gray-600">FB Attorneys Academy</p>
        </div>

        <Card className="p-6 mb-8 border-2 border-secondary">
          <h2 className="text-xl font-bold text-primary mb-4 flex items-center">
            <Info className="w-5 h-5 mr-2 text-secondary" />
            Eligibility Criteria
          </h2>
          <ul className="space-y-2 text-sm text-gray-700">
            {eligibilityCriteria.length > 0 ? (
              eligibilityCriteria.filter(c => c.active !== false).map((criteria, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-secondary mr-2">•</span>
                  <span>{criteria.text}</span>
                </li>
              ))
            ) : (
              <>
                <li className="flex items-start"><span className="text-secondary mr-2">•</span><span>Not above the age of 25 as of the date of submitting this application</span></li>
                <li className="flex items-start"><span className="text-secondary mr-2">•</span><span>Must have scored Division 1 in A levels, or equivalent in certificate or diploma courses</span></li>
                <li className="flex items-start"><span className="text-secondary mr-2">•</span><span>Must possess an LLB with a minimum CGPA of {gpaMins[5]} (on a 5.0 scale) or {gpaMins[4]} (on a 4.0 scale)</span></li>
                <li className="flex items-start"><span className="text-secondary mr-2">•</span><span>Must have completed LLB in 2023 or onwards</span></li>
                <li className="flex items-start"><span className="text-secondary mr-2">•</span><span>Willing to sit for an Academic Assessment on a date to be communicated</span></li>
              </>
            )}
          </ul>
        </Card>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-base font-semibold text-gray-700">Step {step} of {totalSteps}</span>
            <span className="text-base font-semibold text-gray-700">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-3" />
        </div>


        <Card className="p-8">
          <form noValidate onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter' && e.target.type !== 'submit') e.preventDefault(); }}>
            <AnimatePresence mode="wait">

              {/* ── Step 1: Personal Details ── */}
              {step === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.3 }} className="space-y-6">
                  <h2 className="text-2xl font-bold text-primary mb-6">Personal Details</h2>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="middle_name">Middle Name</Label>
                      <Input id="middle_name" name="middle_name" value={formData.middle_name} onChange={handleChange} />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nida_passport">NIDA or Passport ID No *</Label>
                      <Input id="nida_passport" name="nida_passport" value={formData.nida_passport} onChange={handleChange} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="id_expiry">Expiry (if Passport)</Label>
                      <Input id="id_expiry" name="id_expiry" type="date" value={formData.id_expiry} onChange={handleChange} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Upload NIDA/Passport Copy * (MANDATORY)</Label>
                    <p className="text-xs text-gray-600 mb-2">Only JPG and PDF files allowed. Maximum size: 1MB</p>
                    <div className="border-2 border-dashed border-gray-300 rounded-sm p-6 text-center">
                      <input id="nida_passport_copy" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('nida_passport_copy', e.target.files[0])} className="hidden" />
                      <label htmlFor="nida_passport_copy" className="cursor-pointer">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">Click to upload NIDA or Passport copy</p>
                        <p className="text-xs text-gray-500 mt-1">JPG or PDF, max 1MB</p>
                        {formData.nida_passport_copy && <p className="text-sm text-green-600 mt-2 flex items-center justify-center"><CheckCircle className="w-4 h-4 mr-1" /> File uploaded</p>}
                      </label>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date_of_birth">Date of Birth *</Label>
                      <Input id="date_of_birth" name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} required />
                      {age !== null && <p className="text-sm text-gray-600">Age: {age} years (automatically calculated)</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="birth_place">Birth Place *</Label>
                      <Input id="birth_place" name="birth_place" value={formData.birth_place} onChange={handleChange} required />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email" name="email" type="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleEmailBlur}
                        className={emailDuplicate ? 'border-red-500 focus-visible:ring-red-500' : ''}
                        required
                      />
                      {emailChecking && <p className="text-xs text-gray-500">Checking...</p>}
                      {emailDuplicate && (
                        <p className="text-sm text-red-600 font-medium">
                          An application with this email already exists for the current intake. You cannot apply again.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mobile">Mobile Number *</Label>
                      <div className="flex">
                        <span className="inline-flex items-center px-3 border border-r-0 border-input rounded-l-sm bg-muted text-sm font-medium text-gray-700 select-none">+255</span>
                        <Input
                          id="mobile"
                          className="rounded-l-none"
                          value={formData.mobile.replace(/^255/, '')}
                          onChange={handleMobileChange}
                          inputMode="numeric"
                          maxLength={12}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">WhatsApp Number (if different from mobile)</Label>
                    <Input id="whatsapp" name="whatsapp" value={formData.whatsapp} onChange={handleChange} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Residential Address *</Label>
                    <textarea id="address" name="address" value={formData.address} onChange={handleChange} className="w-full px-3 py-2 border border-input rounded-sm min-h-24" required />
                  </div>

                  <div className="space-y-3">
                    <Label>Are you currently employed or in an internship?</Label>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="currently_employed"
                          value="yes"
                          checked={formData.currently_employed === true}
                          onChange={() => setFormData(prev => ({ ...prev, currently_employed: true }))}
                          className="w-4 h-4 accent-secondary"
                        />
                        <span className="text-sm">Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="currently_employed"
                          value="no"
                          checked={formData.currently_employed === false}
                          onChange={() => setFormData(prev => ({ ...prev, currently_employed: false, employer_name: '' }))}
                          className="w-4 h-4 accent-secondary"
                        />
                        <span className="text-sm">No</span>
                      </label>
                    </div>
                    {formData.currently_employed === true && (
                      <div className="space-y-2">
                        <Label htmlFor="employer_name">Name of Employer / Organization *</Label>
                        <Input
                          id="employer_name"
                          name="employer_name"
                          value={formData.employer_name}
                          onChange={handleChange}
                          placeholder="e.g. ABC Law Firm"
                        />
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ── Step 2: O Level Details ── */}
              {step === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.3 }} className="space-y-6">
                  <h2 className="text-2xl font-bold text-primary mb-6">O Level Details</h2>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="o_level_year">Year of Graduation *</Label>
                      <Input id="o_level_year" name="o_level_year" type="number" value={formData.o_level_year} onChange={handleChange} placeholder="e.g., 2018" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="o_level_school">School *</Label>
                      <Input id="o_level_school" name="o_level_school" value={formData.o_level_school} onChange={handleChange} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="o_level_division">Division (if applicable)</Label>
                      <Select value={formData.o_level_division} onValueChange={(val) => setFormData(prev => ({ ...prev, o_level_division: val }))}>
                        <SelectTrigger id="o_level_division">
                          <SelectValue placeholder="Select division (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {O_LEVEL_DIVISION_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Upload O Level Results * (MANDATORY)</Label>
                    <p className="text-xs text-gray-600 mb-2">Only JPG and PDF files allowed. Maximum size: 1MB</p>
                    <div className="border-2 border-dashed border-gray-300 rounded-sm p-6 text-center">
                      <input id="o_level_certificate" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('o_level_certificate', e.target.files[0])} className="hidden" />
                      <label htmlFor="o_level_certificate" className="cursor-pointer">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">Click to upload O Level certificate</p>
                        <p className="text-xs text-gray-500 mt-1">JPG or PDF, max 1MB</p>
                        {formData.o_level_certificate && <p className="text-sm text-green-600 mt-2 flex items-center justify-center"><CheckCircle className="w-4 h-4 mr-1" /> File uploaded</p>}
                      </label>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── Step 3: A Level & LLB ── */}
              {step === 3 && (
                <motion.div key="step3" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.3 }} className="space-y-6">
                  <h2 className="text-2xl font-bold text-primary mb-6">A Level & LLB Details</h2>

                  {/* A-Level section */}
                  <div className="border-b pb-6 mb-6">
                    <h3 className="text-lg font-bold text-primary mb-4">A Level Details</h3>

                    {/* Have / have-not toggle */}
                    <div className="mb-6">
                      <Label className="mb-2 block">Did you complete A Levels? *</Label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setHasALevel(true)}
                          className={`px-5 py-2 rounded-sm border text-sm font-medium transition-colors ${hasALevel === true ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'}`}
                        >
                          Yes — I completed A Levels
                        </button>
                        <button
                          type="button"
                          onClick={() => setHasALevel(false)}
                          className={`px-5 py-2 rounded-sm border text-sm font-medium transition-colors ${hasALevel === false ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'}`}
                        >
                          No — I have an equivalent qualification
                        </button>
                      </div>
                    </div>

                    {hasALevel !== null && (
                      <>
                        {/* Curriculum field for non-A-level */}
                        {!hasALevel && (
                          <div className="space-y-2 mb-4 p-4 bg-blue-50 rounded-sm border border-blue-200">
                            <Label htmlFor="a_level_curriculum">Curriculum / Qualification Completed *</Label>
                            <Input
                              id="a_level_curriculum"
                              name="a_level_curriculum"
                              value={formData.a_level_curriculum}
                              onChange={handleChange}
                              placeholder="e.g., International Baccalaureate, Cambridge Pre-U, Advanced Diploma..."
                            />
                            <p className="text-xs text-blue-700">
                              Please provide your top 3 subjects below. The first 3 subjects will be used for evaluation. You may add more using the button below.
                            </p>
                          </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2">
                            <Label htmlFor="a_level_year">Year of Graduation</Label>
                            <Input id="a_level_year" name="a_level_year" type="number" value={formData.a_level_year} onChange={handleChange} placeholder="e.g., 2020" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="a_level_school">School / Institution</Label>
                            <Input id="a_level_school" name="a_level_school" value={formData.a_level_school} onChange={handleChange} />
                          </div>
                        </div>

                        {/* Subject rows */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between mb-1">
                            <Label>{hasALevel ? 'A Level Subjects' : 'Top 3 Subjects'} * <span className="text-gray-500 font-normal text-xs">(first 3 used for scoring)</span></Label>
                          </div>
                          <div className="grid grid-cols-12 gap-2 mb-1 px-1">
                            <span className="col-span-1 text-xs text-gray-400">#</span>
                            <span className="col-span-7 text-xs text-gray-500 font-medium">Subject</span>
                            <span className="col-span-3 text-xs text-gray-500 font-medium">Grade</span>
                          </div>
                          {formData.a_level_subjects.map((subj, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                              <span className={`col-span-1 text-sm text-center font-medium ${idx < 3 ? 'text-primary' : 'text-gray-400'}`}>
                                {idx + 1}
                              </span>
                              <div className="col-span-7">
                                <Input
                                  value={subj.subject}
                                  onChange={(e) => handleSubjectChange(idx, 'subject', e.target.value)}
                                  placeholder={idx < 3 ? `Subject ${idx + 1} (required)` : `Subject ${idx + 1} (optional)`}
                                />
                              </div>
                              <div className="col-span-3">
                                <Select value={subj.grade} onValueChange={(val) => handleSubjectChange(idx, 'grade', val)}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Grade" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {GRADE_OPTIONS.map(g => (
                                      <SelectItem key={g} value={g}>{g}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              {idx >= 3 && (
                                <button
                                  type="button"
                                  onClick={() => removeSubject(idx)}
                                  className="col-span-1 text-red-400 hover:text-red-600 flex items-center justify-center"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={addSubject}
                            className="mt-2 flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium"
                          >
                            <Plus className="w-4 h-4" /> Add more subjects
                          </button>
                        </div>

                        {/* Certificate upload */}
                        <div className="space-y-2 mt-4">
                          <Label>{hasALevel ? 'Upload A Level Certificate * (MANDATORY)' : 'Upload Qualification Certificate (optional)'}</Label>
                          <p className="text-xs text-gray-600 mb-2">Only JPG and PDF files allowed. Maximum size: 1MB</p>
                          <div className="border-2 border-dashed border-gray-300 rounded-sm p-6 text-center">
                            <input id="a_level_certificate" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('a_level_certificate', e.target.files[0])} className="hidden" />
                            <label htmlFor="a_level_certificate" className="cursor-pointer">
                              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                              <p className="text-sm text-gray-600">Click to upload {hasALevel ? 'A Level' : 'qualification'} certificate</p>
                              <p className="text-xs text-gray-500 mt-1">JPG or PDF, max 1MB</p>
                              {formData.a_level_certificate && <p className="text-sm text-green-600 mt-2 flex items-center justify-center"><CheckCircle className="w-4 h-4 mr-1" /> File uploaded</p>}
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* LLB section */}
                  <div>
                    <h3 className="text-lg font-bold text-primary mb-4">LLB Details</h3>

                    {/* GPA Scale selector */}
                    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-sm">
                      <Label className="mb-2 block font-semibold">GPA Scoring Scale *</Label>
                      <p className="text-xs text-gray-600 mb-3">Select the maximum GPA scale used by your institution.</p>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, llb_gpa_scale: '4' }))}
                          className={`px-5 py-2 rounded-sm border text-sm font-medium transition-colors ${formData.llb_gpa_scale === '4' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'}`}
                        >
                          4.0 Scale <span className="text-xs opacity-75">(min GPA {gpaMins[4]})</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, llb_gpa_scale: '5' }))}
                          className={`px-5 py-2 rounded-sm border text-sm font-medium transition-colors ${formData.llb_gpa_scale === '5' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'}`}
                        >
                          5.0 Scale <span className="text-xs opacity-75">(min GPA {gpaMins[5]})</span>
                        </button>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="llb_graduation_date">LLB Graduation Date * (2023 or later)</Label>
                        <Input id="llb_graduation_date" name="llb_graduation_date" type="date" value={formData.llb_graduation_date} onChange={handleChange} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="llb_class">Class (e.g., First Class)</Label>
                        <Input id="llb_class" name="llb_class" value={formData.llb_class} onChange={handleChange} placeholder="e.g., First Class, Second Class Upper" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="llb_cgpa">
                          CGPA * {formData.llb_gpa_scale ? `(Min ${gpaMinLabel} on ${formData.llb_gpa_scale}.0 scale)` : ''}
                        </Label>
                        <Input id="llb_cgpa" name="llb_cgpa" type="number" step="0.01" value={formData.llb_cgpa} onChange={handleChange} placeholder={formData.llb_gpa_scale === '4' ? 'e.g., 3.2' : 'e.g., 3.8'} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="llb_years_attended">Years Attended *</Label>
                        <Input id="llb_years_attended" name="llb_years_attended" type="number" value={formData.llb_years_attended} onChange={handleChange} placeholder="e.g., 3" required />
                      </div>
                    </div>

                    <div className="space-y-2 mt-4">
                      <Label htmlFor="llb_institute">Name of Institute *</Label>
                      <Input id="llb_institute" name="llb_institute" value={formData.llb_institute} onChange={handleChange} required />
                    </div>

                    <div className="space-y-2 mt-4">
                      <Label>Upload LLB Certificate/Transcript * (MANDATORY)</Label>
                      <p className="text-xs text-gray-600 mb-2">Only JPG and PDF files allowed. Maximum size: 1MB</p>
                      <div className="border-2 border-dashed border-gray-300 rounded-sm p-6 text-center">
                        <input id="llb_certificate" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload('llb_certificate', e.target.files[0])} className="hidden" />
                        <label htmlFor="llb_certificate" className="cursor-pointer">
                          <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-600">Click to upload LLB certificate</p>
                          <p className="text-xs text-gray-500 mt-1">JPG or PDF, max 1MB</p>
                          {formData.llb_certificate && <p className="text-sm text-green-600 mt-2 flex items-center justify-center"><CheckCircle className="w-4 h-4 mr-1" /> File uploaded</p>}
                        </label>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── Step 4: Referees ── */}
              {step === 4 && (
                <motion.div key="step4" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.3 }} className="space-y-6">
                  <h2 className="text-2xl font-bold text-primary mb-6">Referees</h2>
                  <p className="text-gray-600 mb-6">
                    Provide details of two academic or professional referees who can comment on your intellectual ability, integrity, and suitability for a competitive legal training environment.
                  </p>

                  <div className="bg-muted p-6 rounded-sm">
                    <h3 className="text-lg font-bold text-primary mb-4">Referee 1</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Full Name *</Label><Input name="referee1_name" value={formData.referee1_name} onChange={handleChange} required /></div>
                      <div className="space-y-2"><Label>Title / Position *</Label><Input name="referee1_title" value={formData.referee1_title} onChange={handleChange} placeholder="e.g., Professor, Senior Partner" required /></div>
                      <div className="space-y-2"><Label>Institution / Organisation *</Label><Input name="referee1_institution" value={formData.referee1_institution} onChange={handleChange} required /></div>
                      <div className="space-y-2"><Label>Email Address *</Label><Input name="referee1_email" type="email" value={formData.referee1_email} onChange={handleChange} required /></div>
                      <div className="space-y-2 md:col-span-2"><Label>Telephone *</Label><Input name="referee1_telephone" value={formData.referee1_telephone} onChange={handleChange} required /></div>
                    </div>
                  </div>

                  <div className="bg-muted p-6 rounded-sm">
                    <h3 className="text-lg font-bold text-primary mb-4">Referee 2</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Full Name *</Label><Input name="referee2_name" value={formData.referee2_name} onChange={handleChange} required /></div>
                      <div className="space-y-2"><Label>Title / Position *</Label><Input name="referee2_title" value={formData.referee2_title} onChange={handleChange} placeholder="e.g., Professor, Senior Partner" required /></div>
                      <div className="space-y-2"><Label>Institution / Organisation *</Label><Input name="referee2_institution" value={formData.referee2_institution} onChange={handleChange} required /></div>
                      <div className="space-y-2"><Label>Email Address *</Label><Input name="referee2_email" type="email" value={formData.referee2_email} onChange={handleChange} required /></div>
                      <div className="space-y-2 md:col-span-2"><Label>Telephone *</Label><Input name="referee2_telephone" value={formData.referee2_telephone} onChange={handleChange} required /></div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── Step 5: Personal Statement & Declarations ── */}
              {step === 5 && (
                <motion.div key="step5" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.3 }} className="space-y-6">
                  <h2 className="text-2xl font-bold text-primary mb-6">Personal Statement & Declarations</h2>

                  <div className="bg-muted p-6 rounded-sm mb-6">
                    <h3 className="text-xl font-bold text-primary mb-4">Personal Statement <span className="text-sm font-normal text-gray-500">(Optional)</span></h3>
                    <p className="text-sm text-gray-600 mb-3">Briefly describe why you want to join FB Attorneys Academy and what you hope to achieve.</p>
                    <textarea
                      name="personal_statement"
                      value={formData.personal_statement}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Write your personal statement here..."
                      className="w-full border rounded-sm p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-secondary"
                    />
                  </div>

                  <div className="bg-muted p-6 rounded-sm">
                    <h3 className="text-xl font-bold text-primary mb-4">Declarations and Undertakings</h3>
                    <p className="text-sm text-gray-600 mb-6">Please read and confirm each declaration by checking the box:</p>

                    <div className="space-y-4">
                      {declarations.map((declaration, index) => (
                        <div key={declaration.id} className="flex items-start space-x-3 p-3 bg-white rounded-sm">
                          <Checkbox
                            id={`declaration_${declaration.id}`}
                            checked={formData.declaration_responses[declaration.id] || false}
                            onCheckedChange={(checked) => setFormData(prev => ({
                              ...prev,
                              declaration_responses: { ...prev.declaration_responses, [declaration.id]: checked }
                            }))}
                          />
                          <label htmlFor={`declaration_${declaration.id}`} className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                            {index + 1}. {declaration.has_link && declaration.link_text ? (
                              <>
                                {declaration.text.split(declaration.link_text)[0]}
                                <Link to={declaration.link_url || '/terms-and-conditions'} target="_blank" className="text-secondary underline inline-flex items-center">
                                  {declaration.link_text}
                                  <ExternalLink className="w-3 h-3 ml-1" />
                                </Link>
                                {declaration.text.split(declaration.link_text)[1] || ''}
                              </>
                            ) : declaration.text}
                            {declaration.mandatory !== false && <span className="text-red-600"> *</span>}
                            {declaration.mandatory === false && <span className="text-gray-500"> (Optional)</span>}
                          </label>
                        </div>
                      ))}
                    </div>

                    {/* Applicant confirmation (no signature) */}
                    <div className="mt-6 pt-6 border-t">
                      <h4 className="font-bold text-primary mb-4">Applicant Confirmation</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Applicant Full Name</Label>
                          <Input value={`${formData.first_name} ${formData.middle_name ? formData.middle_name + ' ' : ''}${formData.last_name}`} disabled className="bg-gray-100" />
                        </div>
                        <div className="space-y-2">
                          <Label>Date</Label>
                          <Input type="date" value={formData.declaration_date} readOnly className="bg-gray-100 cursor-not-allowed" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6">
                    <h3 className="font-bold text-blue-900 mb-2">Before You Submit</h3>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Ensure all required fields are filled accurately</li>
                      <li>• All uploaded documents must be clear and readable (max 1MB each)</li>
                      <li>• You will receive an email confirmation upon submission</li>
                      <li>• Eligible candidates will receive an academic assessment link</li>
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex justify-between mt-8">
              {step > 1 ? (
                <Button type="button" onClick={handleBack} variant="outline" className="flex items-center space-x-2">
                  <ArrowLeft className="w-4 h-4" /><span>Back</span>
                </Button>
              ) : (
                <Button type="button" onClick={() => navigate('/')} variant="outline">Cancel</Button>
              )}

              {step < totalSteps ? (
                <Button type="button" onClick={handleNext} className="bg-secondary text-white hover:bg-secondary/90 flex items-center space-x-2">
                  <span>Next</span><ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button type="submit" className="bg-secondary text-white hover:bg-secondary/90" disabled={loading || !step5Ready}>
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
              )}
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ApplicationForm;
